import { GamescheduleMatchList } from "@/components/Gameschedule/GamescheduleMatchList";
import { MatchWithTeamsAndVenueAndGamedayAndSeason } from "@/models/Match";
import { Season } from "@/models/Season";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { IconType } from "react-icons";
import {
  FiChevronRight,
  FiFacebook,
  FiGlobe,
  FiInstagram,
  FiMail,
  FiYoutube,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { filterPlayersAndCoaches, getSeasonString } from "../../helper/util";
import { LeaguePlayer } from "../../models/LeaguePlayer";
import { FullLeagueTeam } from "../../models/LeagueTeam";
import {
  PlayerImage,
  TeamDetailImage,
  TeamImage,
  VenueImage,
} from "../common/Images";
import { DocumentLink } from "../common/DocumentLink";
import { ResponsiveContainer } from "../common/ResponsiveContainer";
import { IconTikTok, TicketSVG } from "../common/SVG";

interface TeamDetailProps {
  team: FullLeagueTeam;
  players: LeaguePlayer[];
  baseUrl: string;
  matches: MatchWithTeamsAndVenueAndGamedayAndSeason[];
  currentSeason: Season;
}
export interface SocmedProps {
  url: string | null | undefined;
  icon: IconType;
  name: string;
  isMail?: boolean;
}

export function TeamDetail({
  team,
  players: allPlayers,
  baseUrl,
  currentSeason,
  matches,
}: TeamDetailProps) {
  const { t } = useTranslation();
  const { coaches, players } = filterPlayersAndCoaches(allPlayers);
  const socmeds: SocmedProps[] = [
    { url: team.seasonTeam.email, icon: FiMail, name: "eMail", isMail: true },
    { url: team.seasonTeam.websiteLink, icon: FiGlobe, name: "Website" },
    { url: team.seasonTeam.facebookLink, icon: FiFacebook, name: "Facebook" },
    {
      url: team.seasonTeam.instagramLink,
      icon: FiInstagram,
      name: "Instagram",
    },
    { url: team.seasonTeam.tiktokLink, icon: IconTikTok, name: "TikTok" },
    { url: team.seasonTeam.youtubeLink, icon: FiYoutube, name: "YouTube" },
    {
      url: team.seasonTeam.whatsappLink,
      icon: FaWhatsapp,
      name: "WhatsApp",
    },
    { url: team.seasonTeam.ticketshopLink, icon: TicketSVG, name: "Tickets" },
  ];
  return (
    <>
      <div className="container container-padding-responsive lg:text-xl mb-4">
        <ResponsiveContainer>
          <div title={t("The_Team") as string}>
            <TeamDetailImage team={team.seasonTeam} />
          </div>
          <div title={t("Adresse & Links") as string}>
            <div className="bg-white lg:bg-bg-gray p-4 lg:p-0 mb-8">
              {team.seasonTeam.logoPngUrl && (
                <TeamImage team={team.seasonTeam} size="lg" className="mb-4" />
              )}
              {team.seasonTeam.adress && team.seasonTeam.zipCode && (
                <>
                  <div className="font-bold">{team.seasonTeam.name}</div>
                  <div>{team.seasonTeam.adress}</div>
                  <div>
                    {team.seasonTeam.zipCode +
                      " " +
                      team.seasonTeam.adressPlace}
                  </div>
                </>
              )}
              <div className="my-4 lg:text-lg font-thin">
                {team.seasonTeam.phoneNumber}
              </div>
              <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:space-x-10 lg:space-y-0 lg:py-4">
                {socmeds.map((socmed, i) => {
                  return (
                    socmed.url && (
                      <Link
                        key={i}
                        href={`${socmed.isMail ? "mailto:" : ""}${socmed.url}`}
                        target={!socmed.isMail ? "_blank" : undefined}
                        className="flex gap-4 lg:block"
                      >
                        {<socmed.icon size={24} />}
                        <div className="lg:hidden">{socmed.name}</div>
                      </Link>
                    )
                  );
                })}
              </div>
            </div>
          </div>
        </ResponsiveContainer>
      </div>
      <div className="container container-padding-responsive lg:text-xl mb-4">
        <ResponsiveContainer>
          {team.seasonTeam.venue && (
            <div title={t("Form_Venue") as string}>
              <div className="flex flex-col bg-white lg:bg-bg-gray p-4 lg:p-0">
                <div className="lg:order-last">
                  <VenueImage venue={team.seasonTeam.venue} />
                </div>
                <div className="font-bold ">{team.seasonTeam.venue.name}</div>
                <div className="">{team.seasonTeam.venue.adress}</div>
                <div className="lg:mb-4">
                  {team.seasonTeam.venue.zipCode} {team.seasonTeam.venue.place}
                </div>
              </div>
            </div>
          )}
          {(team.seasonTeam.outfitter ||
            team.seasonTeam.outfitterTwo ||
            team.seasonTeam.foundingYear ||
            team.seasonTeam.playingSince ||
            team.seasonTeam.bundesligaTitles ||
            team.seasonTeam.pokalTitles ||
            team.seasonTeam.internationalTitles) && (
            <div
              className="bg-white lg:bg-bg-gray p-4 lg:p-0"
              title={t("Team_Infos") as string}
            >
              {(team.seasonTeam.outfitter || team.seasonTeam.outfitterTwo) && (
                <InfoCard
                  title={t("Outfitter")}
                  text={
                    [
                      team.seasonTeam.outfitter?.company,
                      team.seasonTeam.outfitterTwo?.company,
                    ].filter(Boolean) as string[]
                  }
                  href={
                    [
                      team.seasonTeam.outfitterLink ||
                        team.seasonTeam.outfitter?.website,
                      team.seasonTeam.outfitterTwoLink ||
                        team.seasonTeam.outfitterTwo?.website,
                    ].filter(Boolean) as string[]
                  }
                />
              )}
              {team.seasonTeam.foundingYear && (
                <InfoCard
                  title={t("Form_Founding_Year")}
                  text={team.seasonTeam.foundingYear.toString()}
                />
              )}
              {team.seasonTeam.playingSince && (
                <InfoCard
                  title={t("Form_Playing_Since")}
                  text={team.seasonTeam.playingSince.toString()}
                />
              )}
              {team.seasonTeam.bundesligaTitles && (
                <InfoCard
                  title={t("Form_Bundesliga_Titles")}
                  text={team.seasonTeam.bundesligaTitles}
                />
              )}
              {team.seasonTeam.pokalTitles && (
                <InfoCard
                  title={t("Form_Pokal_Titles")}
                  text={team.seasonTeam.pokalTitles}
                />
              )}
              {team.seasonTeam.internationalTitles && (
                <InfoCard
                  title={t("Form_International_Titles")}
                  text={team.seasonTeam.internationalTitles}
                />
              )}
            </div>
          )}
        </ResponsiveContainer>
      </div>

      {/* -------------------- COACHES -------------------- */}
      <div className="container container-padding-responsive lg:text-xl mb-4">
        <ResponsiveContainer>
          <div>
            {coaches[0] && (
              <div
                title={t("Form_Coach") as string}
                className="bg-white lg:p-4"
              >
                <div>
                  {coaches.map((p) => (
                    <CoachCard key={p.id} player={p} baseUrl={baseUrl} />
                  ))}
                </div>
              </div>
            )}

            {players[0] && (
              <div title={t("Players") as string} className="bg-white lg:p-4">
                <div>
                  {players.map((p) => (
                    <PlayerCard key={p.id} player={p} baseUrl={baseUrl} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div title={`${t("Season")} ${getSeasonString(currentSeason)}`}>
            <GamescheduleMatchList matches={matches} documentNavigation />
          </div>
        </ResponsiveContainer>
      </div>
    </>
  );
}

export const InfoCard = ({
  title,
  text,
  href,
  linkIsExternal = true,
}: {
  title: string;
  text: string | string[];
  href?: string | string[];
  linkIsExternal?: boolean;
}) => {
  const textArray = Array.isArray(text) ? text : [text];
  const hrefArray = href ? (Array.isArray(href) ? href : [href]) : [];
  const hasLinks = hrefArray.length > 0;

  return (
    <div className="p-2 mb-2 border-b border-gray-light last:border-none">
      <div className="font-bold">{title}</div>
      {hasLinks ? (
        <div className="text-primary text-xl">
          {textArray.map((item, i) => (
            <span key={i}>
              <a
                href={hrefArray[i] || hrefArray[0]}
                target={linkIsExternal ? "_blank" : undefined}
                className="underline"
              >
                {item}
              </a>
              {i < textArray.length - 1 && ", "}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-primary text-xl">
          {textArray.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </div>
      )}
    </div>
  );
};

interface PlayerCardProps {
  player: LeaguePlayer;
  baseUrl: string;
}
function PlayerCard({ player, baseUrl }: PlayerCardProps) {
  // TODO: Nationality Flag + Link
  return (
    <DocumentLink
      href={`${baseUrl}/players/${player.id}`}
      className="flex group space-x-2 p-4 xl:px-0 border-b border-gray-light last:border-none"
      title={`${player.seasonPlayer.firstName} ${player.seasonPlayer.lastName}`}
    >
      <PlayerImage player={player.seasonPlayer} />
      <div className="flex flex-col justify-between flex-1 h-full align-top">
        <div className="">
          <div className="text-xl">{player.seasonPlayer.firstName}</div>
          <div className=" text-3xl">{player.seasonPlayer.lastName}</div>
        </div>
        <div className=""></div>
      </div>
      <FiChevronRight
        size={24}
        className="group-hover:-translate-x-1 transition-transform duration-200 self-center"
      />
    </DocumentLink>
  );
}

function CoachCard({ player, baseUrl }: PlayerCardProps) {
  // TODO: Nationality Flag + Link
  return (
    <DocumentLink
      href={`${baseUrl}/players/${player.id}`}
      className="flex group space-x-2 p-4 xl:px-0 border-b border-gray-light last:border-none"
    >
      <PlayerImage player={player.seasonPlayer} size="xl" />
      <div className="flex flex-col justify-between flex-1 h-full pt-2">
        <div className="">
          <div className="text-xl">{player.seasonPlayer.firstName}</div>
          <div className=" text-3xl">{player.seasonPlayer.lastName}</div>
        </div>
        <div className=""></div>
      </div>
      <FiChevronRight
        size={24}
        className="group-hover:-translate-x-1 transition-transform duration-200 self-center"
      />
    </DocumentLink>
  );
}
